import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TermDetails } from "../../../api/content/ontologyApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ModalShell from "../../../ui/overlays/ModalShell";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { orderedLabels } from "../viewer/ontologyModel";
import { compactIri } from "../viewer/types";
import ChangeSetPicker from "../../changeSets/ChangeSetPicker";
import type { ChangeSetChoice } from "../../changeSets/changeSetChoice";
import type { ChangeSetStep, StepSave } from "../../processAuthoring/draft/changeSetStep";
import StepSaveNotice from "../../processAuthoring/draft/StepSaveNotice";
import { readDraft, type TermEditDraft, type TermEditRequest } from "./termEditDraft";
import { patchOfTermEdit } from "./termPatches";
import { termEditCommit } from "./termProposals";
import Field from "../../../ui/forms/Field";
import SourceDiff from "../../../ui/content/source/SourceDiff";

interface AddSubclassDialogProps {
  projectId: string;
  details: TermDetails;
  knownIris: readonly string[];
  /** The project's own modules, the only ones an edit may go into. */
  ownModules: ReadonlySet<string>;
  /** The term card's "Edit an item" step, which the proposal answers (ADR-0031 D2). */
  step: ChangeSetStep;
  onClose: () => void;
  onProposed: (proposal: StepSave) => void;
}

const REQUEST: TermEditRequest = { kind: "addSubclass" };

/** Propose a new subclass of a term, its label and IRI, as a triple patch in a change set, for
 * review. Labels and definitions are edited in place on the term instead (F96). */
export default function AddSubclassDialog({ projectId, details, knownIris, ownModules, step, onClose, onProposed }: AddSubclassDialogProps) {
  const { t, i18n } = useTranslation();
  const termName = orderedLabels(details.term.labels, i18n.language)[0]?.value ?? compactIri(details.term.iri);
  const title = t("ontologyViewer.proposals.titles.addSubclass", { name: termName });
  const [draft, setDraft] = useState<TermEditDraft>({ text: "", language: i18n.language.split("-")[0] ?? "en", iri: null });
  const [choice, setChoice] = useState<ChangeSetChoice>({ kind: "new", title });
  const submission = useSubmission();
  const [unchanged, setUnchanged] = useState<StepSave | null>(null);
  const { edit, problem, subclassIri } = readDraft(REQUEST, draft, details.term.iri, new Set(knownIris));
  const patch = problem === null ? patchOfTermEdit(details.term.iri, edit) : null;

  const submit = () => {
    submission.submit(async () => {
      const outcome = await step.save(projectId, choice, [termEditCommit(projectId, details, [edit], ownModules)]);
      if (outcome.kind === "nothingToChange") setUnchanged(outcome);
      else onProposed(outcome);
    });
  };

  return (
    <ModalShell
      title={title}
      subtitle={t("ontologyViewer.proposals.subtitle")}
      onClose={onClose}
      closeDisabled={submission.state.status === "submitting"}
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <AppButton variant="secondary" size="sm" onClick={onClose}>
            {t("common.cancel")}
          </AppButton>
          <AppButton
            variant="primary"
            size="sm"
            disabled={problem !== null || submission.state.status === "submitting" || (choice.kind === "new" && choice.title.trim() === "")}
            onClick={submit}
          >
            {t("ontologyViewer.proposals.propose")}
          </AppButton>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label={t("ontologyViewer.proposals.labelText")}>
          {(control) => (
            <input
              {...control}
              className={FORM_COMPACT_CONTROL_CLASS}
              value={draft.text}
              onChange={(event) => {
                setDraft({ ...draft, text: event.target.value });
              }}
            />
          )}
        </Field>
        <Field label={t("ontologyViewer.proposals.language")} hint={t("ontologyViewer.proposals.languageHint")}>
          {(control) => (
            <input
              {...control}
              className={`${FORM_COMPACT_CONTROL_CLASS} max-w-[8rem]`}
              value={draft.language}
              onChange={(event) => {
                setDraft({ ...draft, language: event.target.value });
              }}
            />
          )}
        </Field>
        <Field label={t("ontologyViewer.proposals.newIri")}>
          {(control) => (
            <input
              {...control}
              className={`${FORM_COMPACT_CONTROL_CLASS} font-mono`}
              value={subclassIri}
              onChange={(event) => {
                setDraft({ ...draft, iri: event.target.value });
              }}
            />
          )}
        </Field>
        {problem !== null && draft.text.trim() !== "" ? <p className="app-error-text text-sm">{t(`ontologyViewer.proposals.problems.${problem}`)}</p> : null}
        <ChangeSetPicker projectId={projectId} choice={choice} onChange={setChoice} defaultTitle={title} />
        {patch !== null ? (
          <div className="space-y-1">
            <h3 className="app-subsection-title text-sm">{t("ontologyViewer.proposals.preview")}</h3>
            <SourceDiff label={t("ontologyViewer.proposals.preview")} language="turtle" original={patch.removeTurtle} modified={patch.addTurtle} />
          </div>
        ) : null}
        {submission.state.status === "failed" ? <ErrorNotice error={submission.state.error} /> : null}
        {unchanged !== null && submission.state.status === "succeeded" ? <StepSaveNotice saved={unchanged} shownIn={step.shownIn} /> : null}
      </div>
    </ModalShell>
  );
}
